// Units as Apsio writes them (docs/brand.md, Type): mono figures with their unit, in the
// user's language for separators.
import type { Translator } from '../i18n'

/**
 * 412 ms, 4.31 s, 38.2 s, 54 s, 3 m 48 s, 9 h 35 min (Console D, APS-339, APS-371): under a
 * minute, three significant digits without trailing zeros (54.0 s reads 54 s, 9.50 s 9.5 s), as
 * every duration in the mock reads; then minutes and seconds; past an hour, hours and minutes.
 */
export function duration(i18n: Translator, ms: number): string {
  if (!Number.isFinite(ms)) return ''
  if (ms < 1000) return `${i18n.number(Math.round(ms))} ms`
  if (ms < 59_950)
    return `${i18n.number(ms / 1000, { minimumSignificantDigits: 1, maximumSignificantDigits: 3 })} s`
  // Whole seconds first, so 3 m 59.6 s reads 4 m, never 3 m 60 s.
  const total = Math.round(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m < 60) return s ? `${m} m ${s} s` : `${m} m`
  const h = Math.floor(m / 60)
  return `${h} h ${m % 60} min`
}

/** A share as the mock writes it, with a space before the sign: 1.1 %, 0.54 %, 99 %. */
export function percentSpaced(i18n: Translator, share: number, digits: number): string {
  return `${i18n.number(share * 100, { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`
}

/**
 * A duration axis in one unit, as the mock's (2.4 s, 1.8, 1.2): seconds when the top passes a
 * second, milliseconds otherwise. Pair it with unitOnTop (LineChart does).
 */
export function durationAxis(i18n: Translator, max: number): (v: number) => string {
  // Two decimals, so a 0.25 s step never rounds to 0.3; LineChart's axisLabels then trims every
  // tick to the decimals the finest one needs (0.0 / 0.5 / 1.0 s; APS-408).
  return max >= 1000
    ? (v) => `${i18n.number(v / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} s`
    : (v) => `${i18n.number(Math.round(v), { useGrouping: false })} ms`
}

/** A count per minute: two decimals below 1 (frozen frames are rare), else one. */
export function perMinute(i18n: Translator, n: number): string {
  return i18n.number(n, { maximumFractionDigits: Math.abs(n) < 1 ? 2 : 1 })
}

/** A signed offset in seconds, for times relative to a moment: −2.00 s, 0.00 s, 3.05 s. */
export function offset(i18n: Translator, ms: number): string {
  if (Math.abs(ms) >= 60_000) return `${ms < 0 ? '−' : ''}${duration(i18n, Math.abs(ms))}`
  const v = i18n.number(Math.abs(ms) / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${ms < 0 ? '−' : ''}${v} s`
}

/** A change in percentage points, signed: −0.33, +0.12. */
export function points(i18n: Translator, delta: number): string {
  const v = i18n.number(Math.abs(delta), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${delta < 0 ? '−' : '+'}${v}`
}

/** Compact counts: 512k, 1.8M. */
export function compact(i18n: Translator, n: number): string {
  return n < 10_000
    ? i18n.number(n)
    : i18n.number(n, { notation: 'compact', maximumFractionDigits: 1 })
}

/** A long id shortened the way the design shows it: 7f3c…e19a. */
export function shortId(id: string, head = 4, tail = 4): string {
  return id.length <= head + tail + 1 ? id : `${id.slice(0, head)}…${id.slice(-tail)}`
}

/** Two initials for the avatar. */
/** A day as a table shows it: "Sep 28" this year, with the year otherwise. */
export function day(i18n: Translator, value: string, now: Date = new Date()): string {
  const d = new Date(value)
  return i18n.date(
    value,
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { dateStyle: 'medium' },
  )
}

/** A day and its time, as a status reads it: "Oct 4, 10:12" this year, with the year otherwise. */
export function dayTime(i18n: Translator, value: string, now: Date = new Date()): string {
  const d = new Date(value)
  return i18n.date(
    value,
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { dateStyle: 'medium', timeStyle: 'short' },
  )
}

export function initials(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) return `${words[0]?.[0] ?? ''}${words.at(-1)?.[0] ?? ''}`.toUpperCase()
  return (words[0] ?? email).slice(0, 2).toUpperCase()
}

/** A result cell as text: NULL marked, maps and arrays as JSON, never as markup. */
export function cellText(value: unknown): { text: string; isNull: boolean } {
  if (value === null || value === undefined) return { text: 'NULL', isNull: true }
  if (typeof value === 'object') return { text: JSON.stringify(value), isNull: false }
  return { text: String(value), isNull: false }
}

/**
 * A URL cut at its own seams, for a line break between the pieces and never inside one (APS-384):
 * after / ? & =, and before {. Joined, the pieces are the URL again.
 */
export function urlSeams(url: string): string[] {
  return url.split(/(?<=[/?&=])|(?=\{)/).filter((piece) => piece !== '')
}
